/**
 * Shared microphone handling for dictation sessions: one stream at a time,
 * released the moment a session ends so the browser's tab "in use" (mic)
 * indicator clears instead of lingering after the user is done.
 */

/** The microphone stream of the in-flight dictation session. */
let sharedMic: MediaStream | null = null

/** getUserMedia errors that can be momentary rather than a permanent failure:
 *  the OS hasn't released the mic from a just-stopped session (rapid stop→start
 *  re-tap), or the device list is briefly empty right after a reset / dev HMR
 *  orphaned the prior stream. We retry once before giving up. */
const TRANSIENT_MIC_ERRORS = new Set([
  'NotReadableError',
  'AbortError',
  'NotFoundError',
])

const errorName = (error: unknown): string =>
  error instanceof DOMException ? error.name : ''

/** i18n key for a getUserMedia failure — only an actual permission/secure-context
 *  block is reported as "access needed"; a busy or missing device gets its own
 *  message so the user isn't told to grant access they've already granted. */
export function micErrorKey(error: unknown): string {
  switch (errorName(error)) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'voice.denied'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'voice.noMic'
    default:
      return 'voice.busy'
  }
}

/** Open the microphone for a session, reusing a still-live stream if one is
 *  somehow already open (e.g. two composers racing) rather than stacking a
 *  second getUserMedia. The permission grant itself persists in the browser, so
 *  reacquiring on the next tap is silent on desktop. A rapid re-tap can hit the
 *  mic before the OS releases it from the previous session, so a transient
 *  failure is retried once after a short beat. */
export async function acquireSharedMic(): Promise<MediaStream> {
  const cached = sharedMic
  if (cached?.getAudioTracks().some((track) => track.readyState === 'live')) {
    return cached
  }
  try {
    sharedMic = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch (error) {
    if (!TRANSIENT_MIC_ERRORS.has(errorName(error))) throw error
    await new Promise((resolve) => setTimeout(resolve, 200))
    sharedMic = await navigator.mediaDevices.getUserMedia({ audio: true })
  }
  return sharedMic
}

/** Stop the mic tracks and drop the stream, which removes the tab's recording
 *  indicator. Safe to call when nothing is open. */
export function stopSharedMic(): void {
  sharedMic?.getTracks().forEach((track) => track.stop())
  sharedMic = null
}

if (typeof window !== 'undefined') {
  // Belt-and-braces: release the mic if the tab goes away mid-session.
  window.addEventListener('pagehide', stopSharedMic)
}

// In dev, a hot reload swaps this module while the old `sharedMic` keeps the
// device open — the orphaned stream wedges the mic so the next acquire fails
// (NotFound/NotReadable). Release it on dispose so the new module starts clean.
if (import.meta.hot) {
  import.meta.hot.dispose(stopSharedMic)
}
